import React, { useState } from 'react';
import { CareerApplication } from './CareerApplication';
import { EditorialHero } from './EditorialHero';

export const CareersPage: React.FC = () => {
	const [showApplication, setShowApplication] = useState(false);
	const [selectedPosition, setSelectedPosition] = useState('');

	const jobOpenings = [
		{
			id: 1,
			title: "Lead Full Stack AWS Developer",
			level: "Lead",
			type: "Full-time",
			location: "Remote / Hybrid",
			department: "Engineering",
			badge: "Filled",
			badgeColor: "from-slate-500 to-slate-600",
			filled: true,
			description: "Lead our engineering team as it builds reliable applications on AWS. You will make architecture decisions, mentor developers, and improve our products.",
			requirements: [
				"8+ years of full-stack development experience",
				"Expert-level proficiency in Python and React/Angular",
				"Deep AWS knowledge (Lambda, ECS, RDS, DynamoDB, CloudFormation)",
				"Experience adding AI services and APIs to applications",
				"Strong leadership and mentoring skills",
				"Experience with microservices architecture",
				"Knowledge of CI/CD pipelines and DevOps practices"
			],
			responsibilities: [
				"Lead technical architecture decisions and system design",
				"Mentor and guide development team members",
				"Collaborate with product and design teams",
				"Ensure code quality and best practices",
				"Choose new tools when they solve a clear problem"
			],
			benefits: [
				"Competitive salary with equity options",
				"Flexible remote work environment",
				"Professional development budget",
				"Health, dental, and vision coverage",
				"Unlimited PTO and flexible hours"
			]
		},
		{
			id: 2,
			title: "Senior Full Stack AWS Developer",
			level: "Senior",
			type: "Full-time",
			location: "Remote / Hybrid",
			department: "Engineering",
			badge: "Filled",
			badgeColor: "from-slate-500 to-slate-600",
			filled: true,
			description: "Join our engineering team to build applications on AWS. You will work on complex features, improve performance, and help make technical decisions.",
			requirements: [
				"5+ years of full-stack development experience",
				"Strong proficiency in Python and React/Angular",
				"Solid AWS experience (Lambda, ECS, RDS, DynamoDB)",
				"Experience adding AI services to applications",
				"Knowledge of RESTful APIs and microservices",
				"Experience with version control and CI/CD",
				"Strong problem-solving and analytical skills"
			],
			responsibilities: [
				"Develop and maintain reliable web applications",
				"Connect applications with AI services and APIs",
				"Improve application speed and reliability",
				"Collaborate with cross-functional teams",
				"Participate in code reviews and technical discussions"
			],
			benefits: [
				"Competitive salary with growth opportunities",
				"Remote-first work environment",
				"Learning and development resources",
				"Comprehensive benefits package",
				"Flexible work arrangements"
			]
		},
		{
			id: 3,
			title: "HR Manager",
			level: "Manager",
			type: "Full-time",
			location: "Remote / Hybrid",
			department: "Human Resources",
			badge: "Filled",
			badgeColor: "from-slate-500 to-slate-600",
			filled: true,
			description: "Lead our human resources work, manage recruitment, and help create a supportive workplace for the team.",
			requirements: [
				"5+ years of HR experience in tech companies",
				"Experience with remote team management",
				"Strong recruitment and talent acquisition skills",
				"Knowledge of HR best practices and compliance",
				"Experience with HRIS and recruitment tools",
				"Excellent communication and interpersonal skills",
				"Bachelor's degree in HR or related field"
			],
			responsibilities: [
				"Develop and implement HR strategies and policies",
				"Lead recruitment and talent acquisition efforts",
				"Manage employee relations and performance",
				"Oversee benefits and compensation programs",
				"Ensure compliance with labor laws and regulations"
			],
			benefits: [
				"Competitive salary with performance bonuses",
				"Remote work flexibility",
				"Professional development opportunities",
				"Comprehensive benefits package",
				"Impact on company culture and growth"
			]
		}
	];

  return <main className="editorial-page careers-page">
    <EditorialHero eyebrow="Careers" title="Good questions." accent="Great people." description="Help turn ambitious ideas into thoughtful software. Build with curiosity, care, and a sense of purpose." variant="careers" href="#positions" action="Explore our team" />
    <section className="editorial-section team-principles" data-page-reveal>
      <div className="editorial-section-heading"><p className="editorial-label">01 / Working at Cognixis</p><span>Room to do meaningful work.</span></div>
      <div className="editorial-columns">{[
        ['Modern technology.', 'Work with current tools and cloud platforms.'],
        ['Remote first.', 'Work from anywhere with flexible arrangements.'],
        ['A hand in the product.', 'Help research, build, and test new products.'],
      ].map(([title, text], i) => <article key={title}><span className="editorial-label">0{i + 1}</span><h2>{title}</h2><p>{text}</p></article>)}</div>
    </section>
    <section id="positions" className="editorial-section positions" tabIndex={-1}>
      <div className="editorial-section-heading" data-page-reveal><p className="editorial-label">02 / Recent positions</p><span>{jobOpenings.some(job => !job.filled) ? 'Explore opportunities' : 'No open positions at the moment'}</span></div>
      {jobOpenings.map(job => <details className="position" key={job.id} data-page-reveal>
        <summary><span className="editorial-label">0{job.id} / {job.department}</span><h2>{job.title}</h2><span className="position-status">{job.badge}</span><span className="position-plus" aria-hidden="true">+</span></summary>
        <div className="position-body"><p className="position-meta">{job.level} · {job.type} · {job.location}</p><p>{job.description}</p>
          <div className="position-details"><div><h3>Requirements</h3><ul>{job.requirements.map(req => <li key={req}>{req}</li>)}</ul></div><div><h3>Responsibilities</h3><ul>{job.responsibilities.map(resp => <li key={resp}>{resp}</li>)}</ul></div></div>
          <button className="editorial-button" disabled={job.filled} onClick={() => { setSelectedPosition(job.title); setShowApplication(true); }}>{job.filled ? 'Position filled' : 'Apply for this position'}<span aria-hidden="true">↗</span></button>
        </div>
      </details>)}
    </section>
    <section className="editorial-callout" data-page-reveal><p className="editorial-label">A future together</p><h2>Stay curious.<br /><em>Keep in touch.</em></h2><p>We’re not currently accepting applications. Check back for new positions as our team grows.</p><a className="editorial-text-link" href="/blog">Explore our thinking <span aria-hidden="true">↗</span></a></section>
    {showApplication && <CareerApplication position={selectedPosition} onClose={() => setShowApplication(false)} />}
  </main>;
};
